// components/WhatsAppDirect.js - Interfaz completa con detección de idioma español/inglés
import React, { useState, useEffect } from 'react';
import { Icons } from '../utils/icons';
import { authService } from '../services/authService';

const WhatsAppDirect = ({ orden, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('reminder24h');
  const [customMessage, setCustomMessage] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [errorCliente, setErrorCliente] = useState(null);
  const [isSpanishPhone, setIsSpanishPhone] = useState(true);

  // Función para detectar si un teléfono es español
  const detectPhoneCountry = (phone) => {
    if (!phone) return true;
    
    const cleaned = phone.replace(/\D/g, '');
    const isSpanish = cleaned.startsWith('34') || (!cleaned.startsWith('34') && cleaned.length === 9);
    
    console.log(`📞 Detectando país: ${phone} -> ${cleaned} -> Español: ${isSpanish}`);
    return isSpanish;
  };

  // Función para procesar información del cliente
  const procesarInformacionCliente = (clienteData) => {
    if (!clienteData) return null;
    
    let displayName = '';
    if (clienteData["legal-name"] && clienteData["legal-name"].trim()) {
      displayName = clienteData["legal-name"];
    } else if (clienteData.name && clienteData.name.trim()) {
      displayName = clienteData.name;
    } else if (clienteData["full-reference"]) {
      displayName = clienteData["full-reference"];
    } else {
      displayName = `Cliente ${clienteData.id}`;
    }

    const telefono1 = (clienteData.phone && clienteData.phone.trim()) ? clienteData.phone.trim() : '';
    const telefono2 = (clienteData.phone2 && clienteData.phone2.trim()) ? clienteData.phone2.trim() : '';
    const email = (clienteData.email && clienteData.email.trim()) ? clienteData.email.trim() : '';

    let direccionFormateada = '';
    if (clienteData["main-address"]) {
      const addr = clienteData["main-address"];
      const componentes = [];
      if (addr["address-data"]) componentes.push(addr["address-data"]);
      if (addr["city-town"]) componentes.push(addr["city-town"]);
      if (addr.province) componentes.push(addr.province);
      if (addr["postal-code"]) componentes.push(addr["postal-code"]);
      direccionFormateada = componentes.join(', ');
    }

    return {
      displayName,
      telefono: telefono1 || telefono2,
      direccion: direccionFormateada,
      contacto: { telefono1, telefono2, email },
      rawData: clienteData
    };
  };

  // Función para cargar cliente
  const cargarCliente = async () => {
    const accountPath = orden["account-path"];
    
    if (!accountPath) {
      const customerName = orden["customer-name"];
      const customerPhone = orden["customer-phone"];
      
      if (customerName || customerPhone) {
        const clienteFallback = {
          displayName: customerName || 'Cliente',
          telefono: customerPhone || '',
          direccion: orden["customer-address"] || '',
          contacto: { 
            telefono1: customerPhone || '', 
            telefono2: '', 
            email: orden["customer-email"] || '' 
          }
        };
        setClienteInfo(clienteFallback);
        setIsSpanishPhone(detectPhoneCountry(customerPhone));
        return;
      }
      
      setErrorCliente('Esta orden no tiene información de cliente disponible');
      return;
    }

    setLoadingCliente(true);
    setErrorCliente(null);

    try {
      const url = `/.netlify/functions/getClient?accountPath=${encodeURIComponent(accountPath)}`;
      const response = await fetch(url, { headers: { ...authService.authHeader() } });
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.client) {
        const clienteProcesado = procesarInformacionCliente(data.client);
        if (clienteProcesado) {
          setClienteInfo(clienteProcesado);
          setIsSpanishPhone(detectPhoneCountry(clienteProcesado.telefono));
        } else {
          throw new Error('No se pudo procesar la información del cliente');
        }
      } else {
        throw new Error(data.error || 'Error desconocido cargando cliente');
      }
      
    } catch (err) {
      console.error('❌ Error cargando cliente:', err);
      setErrorCliente(`Error cargando cliente: ${err.message}`);
    } finally {
      setLoadingCliente(false);
    }
  };

  // Cargar cliente al abrir el modal
  useEffect(() => {
    cargarCliente();
  }, [orden]);

  // Extraer datos del cliente para WhatsApp
  const getClienteForWhatsApp = () => {
    if (clienteInfo) {
      return {
        nombre: clienteInfo.displayName || 'Cliente',
        telefono: clienteInfo.telefono || '',
        direccion: clienteInfo.direccion || '',
        empresa: clienteInfo.displayName || ''
      };
    }
    
    return {
      nombre: orden["customer-name"] || orden.customerName || 'Cliente',
      telefono: orden["customer-phone"] || orden.customerPhone || '',
      direccion: orden["customer-address"] || orden.customerAddress || '',
      empresa: orden["customer-name"] || orden.customerName || ''
    };
  };

  const cliente = getClienteForWhatsApp();
  const tecnico = orden.tecnico || 'Técnico asignado';
  const fecha = new Date(orden.date).toLocaleDateString(isSpanishPhone ? 'es-ES' : 'en-US');
  const hora = orden["scheduled-time"] || '09:00';
  const empresaNombre = 'Climatización Zapata';

  // Plantillas bilingües con estructura unificada
  const templates = {
    reminder24h: {
      name: isSpanishPhone ? '📅 Recordatorio 24h antes' : '📅 24h Reminder',
      icon: '📅',
      message: isSpanishPhone ? 
        `🔧 ***Recordatorio de Cita - ${empresaNombre}***

¡Hola ${cliente.nombre}! 👋

Te recordamos que tienes una cita programada:

📅 ***Fecha:*** ${fecha}
👨‍🔧 ***Técnico:*** ${tecnico}
📍 ***Dirección:*** ${cliente.direccion}

Si necesitas cambiar la cita, por favor contáctanos.

¡Nos vemos! 😊
https://climatizacionzapata.es/contacto/` :
        `🔧 ***Appointment Reminder - ${empresaNombre}***

Hello ${cliente.nombre}! 👋

We remind you that you have a scheduled appointment:

📅 ***Date:*** ${fecha}
👨‍🔧 ***Technician:*** ${tecnico}
📍 ***Address:*** ${cliente.direccion}

If you need to change the appointment, please contact us.

See you! 😊
https://climatizacionzapata.es/contacto/`
    },
    
    onTheWay: {
      name: isSpanishPhone ? '🚗 Técnico en camino' : '🚗 Technician on the way',
      icon: '🚗',
      message: isSpanishPhone ?
        `🚗 *Tu técnico está en camino*

¡Hola ${cliente.nombre}!

${tecnico} está en camino a tu ubicación.
⏱️ Tiempo estimado de llegada: 15-30 minutos

¡Prepárate que ya llegamos! 🔧

*${empresaNombre}*` :
        `🚗 *Your technician is on the way*

Hello ${cliente.nombre}!

${tecnico} is on the way to your location.
⏱️ Estimated arrival time: 15-30 minutes

Get ready, we're almost there! 🔧

*${empresaNombre}*`
    },
    
    completed: {
      name: isSpanishPhone ? '✅ Trabajo completado' : '✅ Work completed',
      icon: '✅',
      message: isSpanishPhone ?
        `✅ *Trabajo Completado*

¡Hola ${cliente.nombre}!

${tecnico} ha completado el trabajo en tu domicilio.

Gracias por confiar en *${empresaNombre}*. ¡Esperamos haberte ayudado! 😊

Para cualquier consulta, no dudes en contactarnos.` :
        `✅ *Work Completed*

Hello ${cliente.nombre}!

${tecnico} has completed the work at your location.

Thank you for trusting *${empresaNombre}*. We hope we have been helpful! 😊

For any questions, please don't hesitate to contact us.`
    },
    
    confirmation: {
      name: isSpanishPhone ? '📋 Confirmación de cita' : '📋 Appointment confirmation',
      icon: '📋',
      message: isSpanishPhone ?
        `📋 *Confirmación de Cita - ${empresaNombre}*

¡Hola ${cliente.nombre}!

Confirmamos tu cita programada:

📅 *Fecha:* ${fecha}
⏰ *Hora:* ${hora}
👨‍🔧 *Técnico:* ${tecnico}
📍 *Dirección:* ${cliente.direccion}

Por favor, confirma que estarás disponible respondiendo a este mensaje.

¡Gracias! 🔧` :
        `📋 *Appointment Confirmation - ${empresaNombre}*

Hello ${cliente.nombre}!

We confirm your scheduled appointment:

📅 *Date:* ${fecha}
⏰ *Time:* ${hora}
👨‍🔧 *Technician:* ${tecnico}
📍 *Address:* ${cliente.direccion}

Please confirm that you will be available by replying to this message.

Thank you! 🔧`
    }
  };

  // Limpiar y formatear número de teléfono
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    let cleaned = phone.replace(/\D/g, '');
    
    if (!cleaned.startsWith('34') && cleaned.length === 9) {
      cleaned = '34' + cleaned;
    }
    
    if (cleaned.startsWith('340')) {
      cleaned = '34' + cleaned.substring(3);
    }
    
    return '+' + cleaned;
  };

  // Generar URL de WhatsApp
  const generateWhatsAppURL = () => {
    const phoneNumber = formatPhoneNumber(cliente.telefono);
    let message = '';
    
    if (useCustom && customMessage.trim()) {
      message = customMessage.trim();
    } else if (templates[selectedTemplate]) {
      message = templates[selectedTemplate].message;
    }
    
    const encodedMessage = encodeURIComponent(message);
    return `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
  };

  // Abrir WhatsApp
  const openWhatsApp = () => {
    const url = generateWhatsAppURL();
    const idioma = isSpanishPhone ? '🇪🇸 ES' : '🌍 EN';
    console.log(`🚀 Abriendo WhatsApp (${idioma}):`, url);
    window.open(url, '_blank');
  };

  // Copiar enlace al portapapeles
  const copyToClipboard = async () => {
    const url = generateWhatsAppURL();
    try {
      await navigator.clipboard.writeText(url);
      alert('✅ Enlace copiado al portapapeles');
    } catch (err) {
      console.error('Error copiando al portapapeles:', err);
      
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('✅ Enlace copiado al portapapeles');
    }
  };

  const isValidPhone = (phone) => {
    const cleaned = phone?.replace(/\D/g, '');
    return cleaned && cleaned.length >= 9;
  };

  const formattedPhone = formatPhoneNumber(cliente.telefono);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center text-2xl">
                📱
              </div>
              <div>
                <h2 className="text-2xl font-bold">WhatsApp Directo</h2>
                <p className="text-green-100">
                  Gratuito - {isSpanishPhone ? '🇪🇸 Español' : '🌍 English'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-green-500 px-3 py-1 rounded-full text-sm font-bold">
                💰 GRATIS
              </div>
              <div className={`mt-1 px-2 py-1 rounded text-xs ${
                isSpanishPhone ? 'bg-red-500' : 'bg-blue-500'
              }`}>
                {isSpanishPhone ? '🇪🇸 ES' : '🌍 EN'}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-green-800 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Estado de carga del cliente */}
          {loadingCliente && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-lg mb-6">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span>Cargando información del cliente y detectando idioma...</span>
              </div>
            </div>
          )}

          {/* Error cargando cliente */}
          {errorCliente && !loadingCliente && (
            <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-6">
              <div className="flex items-center gap-2">
                <span className="text-red-600">❌</span>
                <div>
                  <div className="font-medium">Error cargando cliente</div>
                  <div className="text-sm text-red-600">{errorCliente}</div>
                </div>
              </div>
            </div>
          )}

          {/* Indicador de idioma detectado */}
          {!loadingCliente && clienteInfo && (
            <div className={`p-4 rounded-lg mb-6 border-2 ${
              isSpanishPhone ? 
                'bg-red-50 border-red-200 text-red-800' : 
                'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{isSpanishPhone ? '🇪🇸' : '🌍'}</span>
                <div>
                  <div className="font-medium">
                    {isSpanishPhone ? 'Teléfono español detectado' : 'International phone detected'}
                  </div>
                  <div className="text-sm">
                    {isSpanishPhone ? 
                      'Los mensajes se enviarán en español automáticamente' :
                      'Messages will be sent in English automatically'
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Información del cliente */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              👤 {isSpanishPhone ? 'Información del Cliente' : 'Customer Information'}
              {loadingCliente && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              )}
            </h3>
            
            {clienteInfo ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">{isSpanishPhone ? 'Nombre:' : 'Name:'}</span>
                  <span className="ml-2 font-medium">{clienteInfo.displayName}</span>
                </div>
                <div>
                  <span className="text-gray-600">{isSpanishPhone ? 'Teléfono:' : 'Phone:'}</span>
                  <span className={`ml-2 font-medium ${isValidPhone(clienteInfo.telefono) ? 'text-green-600' : 'text-red-600'}`}>
                    {clienteInfo.telefono || (isSpanishPhone ? '❌ Sin teléfono' : '❌ No phone')}
                    {isValidPhone(clienteInfo.telefono) && (
                      <span className="ml-1">
                        ✅ {isSpanishPhone ? '🇪🇸' : '🌍'}
                      </span>
                    )}
                  </span>
                </div>
                {clienteInfo.contacto?.telefono2 && (
                  <div>
                    <span className="text-gray-600">{isSpanishPhone ? 'Teléfono 2:' : 'Phone 2:'}</span>
                    <span className="ml-2 font-medium text-blue-600">{clienteInfo.contacto.telefono2}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{clienteInfo.contacto?.email || (isSpanishPhone ? 'No disponible' : 'Not available')}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-600">{isSpanishPhone ? 'Dirección:' : 'Address:'}</span>
                  <span className="ml-2 font-medium">{clienteInfo.direccion || (isSpanishPhone ? 'No disponible' : 'Not available')}</span>
                </div>
              </div>
            ) : !loadingCliente && (
              <div className="text-gray-500 text-sm">
                {errorCliente ? 
                  (isSpanishPhone ? 'No se pudo cargar la información del cliente' : 'Could not load customer information') : 
                  (isSpanishPhone ? 'Información del cliente no disponible' : 'Customer information not available')
                }
              </div>
            )}
          </div>

          {/* Información de la orden */}
          <div className="bg-purple-50 rounded-xl p-4 mb-6 border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-3">
              📋 {isSpanishPhone ? 'Información de la Orden' : 'Order Information'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-purple-600">{isSpanishPhone ? 'Orden:' : 'Order:'}</span>
                <span className="ml-2 font-medium">{orden["full-reference"] || `#${orden.id}`}</span>
              </div>
              <div>
                <span className="text-purple-600">{isSpanishPhone ? 'Fecha:' : 'Date:'}</span>
                <span className="ml-2 font-medium">{fecha}</span>
              </div>
              <div>
                <span className="text-purple-600">{isSpanishPhone ? 'Técnico:' : 'Technician:'}</span>
                <span className="ml-2 font-medium">{tecnico}</span>
              </div>
              <div>
                <span className="text-purple-600">{isSpanishPhone ? 'Hora:' : 'Time:'}</span>
                <span className="ml-2 font-medium">{hora}</span>
              </div>
            </div>
          </div>

          {/* Selección de plantilla */}
          {!useCustom && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">
                📝 {isSpanishPhone ? 'Plantilla de mensaje' : 'Message template'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(templates).map(([key, template]) => (
                  <div
                    key={key}
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${
                      selectedTemplate === key 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                    onClick={() => setSelectedTemplate(key)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{template.icon}</span>
                      <span className="font-medium text-sm">{template.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mensaje personalizado */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="useCustom"
                checked={useCustom}
                onChange={(e) => setUseCustom(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="useCustom" className="font-semibold text-gray-800">
                ✏️ {isSpanishPhone ? 'Usar mensaje personalizado' : 'Use custom message'}
              </label>
            </div>

            {useCustom && (
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder={isSpanishPhone ? 
                  `Escribe tu mensaje personalizado aquí...\n\nEjemplo:\n¡Hola ${cliente.nombre}! Te recordamos tu cita de mañana a las ${hora} con ${tecnico}. ¡Nos vemos!` :
                  `Write your custom message here...\n\nExample:\nHello ${cliente.nombre}! We remind you of your appointment tomorrow at ${hora} with ${tecnico}. See you!`
                }
                className="w-full p-3 border border-gray-300 rounded-lg resize-none h-32"
              />
            )}
          </div>

          {/* Vista previa del mensaje */}
          {!useCustom && selectedTemplate && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                👁️ {isSpanishPhone ? 'Vista previa del mensaje' : 'Message preview'}
                <span className="text-sm">
                  {isSpanishPhone ? '🇪🇸' : '🌍'}
                </span>
              </h4>
              <div className="bg-white p-3 rounded-lg text-sm whitespace-pre-line max-h-40 overflow-y-auto border">
                {templates[selectedTemplate].message}
              </div>
            </div>
          )}

          {/* Advertencias */}
          {!isValidPhone(cliente.telefono) && !loadingCliente && (
            <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-4">
              ❌ {isSpanishPhone ? 
                'No se puede enviar: número de teléfono inválido o faltante' :
                'Cannot send: invalid or missing phone number'
              }
              {!clienteInfo && !errorCliente && (
                <div className="text-xs mt-1">
                  {isSpanishPhone ? 
                    'Los datos del cliente se están cargando...' :
                    'Customer data is loading...'
                  }
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              📱 {isSpanishPhone ? 'Se enviará vía WhatsApp al' : 'Will be sent via WhatsApp to'} {formattedPhone || cliente.telefono || (isSpanishPhone ? 'número a cargar...' : 'number loading...')}
              <span className="ml-2 font-semibold">
                {isSpanishPhone ? '🇪🇸 ES' : '🌍 EN'}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={copyToClipboard}
                disabled={!isValidPhone(cliente.telefono) || loadingCliente}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 text-sm"
              >
                📋 {isSpanishPhone ? 'Copiar enlace' : 'Copy link'}
              </button>
              <button
                onClick={openWhatsApp}
                disabled={!isValidPhone(cliente.telefono) || loadingCliente}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loadingCliente ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {isSpanishPhone ? 'Cargando...' : 'Loading...'}
                  </>
                ) : (
                  <>
                    📱 {isSpanishPhone ? 'Abrir WhatsApp' : 'Open WhatsApp'}
                    <span className="text-xs">
                      {isSpanishPhone ? '🇪🇸' : '🌍'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppDirect;